// Shared synchronous validation for ModuleAddForm and ModuleEditForm.
//
// These rules mirror what the API does in models/Module.js so the user gets
// clear, inline feedback BEFORE submitting instead of a 500 / "MODULE ERROR"
// code coming back from the server:
//   - empty SN -> backend crashes on locker.SN.padStart(...)
//   - locker count not in VALID_LOCKER_COUNTS -> backend writes a broken
//     "MODULE ERROR: ..." Code
//   - Code / Mode / Wide / Espressif are required by the schema

// Valid number of lockers per module type (Code prefix). The backend
// Code-builder supports 1/2/4/6/8/10/12; these are the operational subsets.
export const LOCKER_COUNTS_BY_PREFIX = {
  VLM: [1, 2, 4, 6, 10, 12], // módulos
  VLK: [4, 6, 8],            // kioscos
};

// Union of all backend-supported counts (kept for any generic usage).
export const VALID_LOCKER_COUNTS = [1, 2, 4, 6, 8, 10, 12];

export function getValidLockerCounts(prefix) {
  return LOCKER_COUNTS_BY_PREFIX[prefix] || VALID_LOCKER_COUNTS;
}

const SERIAL_REGEX = /^\d{1,5}$/;
const SN_REGEX = /^\d{1,10}$/;
const PIN_REGEX = /^\d{4}$/;

const isBlankSelect = (v) => v === undefined || v === null || v === '';

// True when there are no lockers, or any locker is missing its SN. Used to
// disable the "Guardar" button until every taquilla has an SN.
export function hasMissingSN(registerLockers) {
  const lockers = Array.isArray(registerLockers) ? registerLockers : [];
  if (lockers.length === 0) return true;
  return lockers.some((l) => !l || l.SN == null || String(l.SN).trim() === '');
}

export function validateModuleForm(values) {
  const errors = {};

  // ── Configuración ───────────────────────────────────────────
  // Code is assembled from Prefix (VLM/VLK) + Serial (5 digits).
  if (!values.Prefix) {
    errors.Prefix = 'Selecciona el tipo (VLM/VLK)';
  }
  const serial = values.Serial != null ? String(values.Serial).trim() : '';
  if (!serial) {
    errors.Serial = 'El número de serie es obligatorio';
  } else if (!SERIAL_REGEX.test(serial)) {
    errors.Serial = 'Solo dígitos (máx. 5)';
  }

  if (isBlankSelect(values.ModeType)) {
    errors.ModeType = 'Selecciona un modo';
  }
  if (isBlankSelect(values.Mode)) {
    errors.Mode = 'Selecciona un formato';
  }
  if (isBlankSelect(values.Wide)) {
    errors.Wide = 'Selecciona un ancho';
  }
  if (isBlankSelect(values.Espressif)) {
    errors.Espressif = 'Selecciona un espressif';
  }
  if (isBlankSelect(values.HardType)) {
    errors.HardType = 'Selecciona un tipo de cierre';
  }

  // ── Taquillas (RegisterLockers) ─────────────────────────────
  const lockers = Array.isArray(values.RegisterLockers) ? values.RegisterLockers : [];
  const lockerErrors = [];
  const seenSN = {};

  lockers.forEach((locker, index) => {
    const rowErrors = {};
    const sn = locker && locker.SN != null ? String(locker.SN).trim() : '';
    const pin = locker && locker.Pin != null ? String(locker.Pin).trim() : '';

    if (!sn) {
      rowErrors.SN = 'SN obligatorio';
    } else if (!SN_REGEX.test(sn)) {
      rowErrors.SN = 'Solo dígitos (máx. 10)';
    } else {
      const padded = sn.padStart(10, '0');
      if (seenSN[padded] !== undefined) {
        rowErrors.SN = `SN duplicado (fila ${seenSN[padded] + 1})`;
      } else {
        seenSN[padded] = index;
      }
    }

    // PIN is optional (backend defaults empty/0 to "2903"), but if the user
    // types one it must be exactly 4 digits.
    if (pin && !PIN_REGEX.test(pin)) {
      rowErrors.Pin = 'PIN de 4 dígitos';
    }

    if (Object.keys(rowErrors).length) {
      lockerErrors[index] = rowErrors;
    }
  });

  const validCounts = getValidLockerCounts(values.Prefix);
  if (lockers.length === 0) {
    lockerErrors._error = 'Añade al menos una taquilla';
  } else if (!validCounts.includes(lockers.length)) {
    lockerErrors._error =
      `El número de taquillas para ${values.Prefix || 'VLM'} debe ser ` +
      `${validCounts.join(', ')} (actualmente: ${lockers.length})`;
  }

  if (lockerErrors.length || lockerErrors._error) {
    errors.RegisterLockers = lockerErrors;
  }

  return errors;
}

export default validateModuleForm;
